import { Injectable, Signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatestWith, map, of, switchMap } from 'rxjs';
import { last } from 'lodash-es';
import {
	DataService,
	FilterExpression,
	AanwezigheidMeasure,
	CompoundFilterExpression,
	BasicFilterExpression,
	flattenFilters,
	mapFilterAttrs,
} from './data.service';
import { AggregatorConfig } from '../shared/dashboard/base-dashboard/aggregator-config';
import { sumOver } from './aggregation';
import { createAggFunctions } from '../shared/dashboard/base-dashboard/base-dashboard.component';
import { unnest, Path, getLeafA } from './data-tree';
import { Attributes } from '../shared/dashboard/base-dashboard/base-dashboard-config';
import { MetricsData } from '../shared/dashboard/vbarchart-table/metrics-series/metrics-series.component';
import { getMaandLabelLang } from '@cumlaude/shared-utils';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { AutorisatieService } from './autorisatie.service';
import { FilterService } from './filter.service';
import { UrlService } from './url.service';
import { UserService } from './user.service';
import { AttrPath } from '@cumlaude/metadata';
import { AW_AFSPRAAK_EQUIVALENTEN, EquivalentAttService } from './equivalent-att.service';
import { FilterName } from './filter-name';

interface LessenI extends Attributes {
	aw_m_nr_lessen: number;
	aw_m_nr_lessen_presentieregistratie_verwerkt: number;
}

type LessenA = LessenI;

const DATAVOLLEDIGHEID_URL = '/aanwezigheid/datavolledigheid';

const VERPLICHT_AFSPRAAK = 'aw_fun_is_presentieregistratie_verplicht_afspraak';

/**
 * De tegel telt lessen (count distinct afspraak), terwijl de filters van het dashboard over de leerling
 * gaan. Filteren op het vak van de leerling levert hier "lessen op lesuren waarop de leerling onder meer
 * Duits had"; bedoeld is "lessen Duits". Daarom wordt elk filter omgezet naar de lesvariant.
 *
 * Dat is niet alleen netter: zonder de vertaling hangt de betekenis van het filter af van de gekozen
 * aggregatie. Op fac_aw_aanwezigheid_agg_maand_lessen komt aw_fun_nm_vak uit de vakkolom van de les, op
 * de basistabel uit de vakken van de leerling. Eén extra filter (bijvoorbeeld op datum) wisselt de
 * aggregatie en daarmee het getal.
 */
const LESVARIANT_PAD = new Map<string, AttrPath>(AW_AFSPRAAK_EQUIVALENTEN.map(([leerling, les]) => [leerling, <AttrPath>les.split('.')]));

@Injectable({
	providedIn: 'root',
})
export class AfgeslotenLessenService {
	private readonly dataService = inject(DataService);
	private readonly autorisatieService = inject(AutorisatieService);
	private readonly filterService = inject(FilterService);
	private readonly urlService = inject(UrlService);
	private readonly userService = inject(UserService);
	private readonly router = inject(Router);
	private readonly equivalentAttService = inject(EquivalentAttService);

	private readonly afgeslotenLessenAggregators: AggregatorConfig<LessenI, LessenA> = {
		getSingleAggregators: () => ({
			aw_m_nr_lessen: sumOver('aw_m_nr_lessen'),
			aw_m_nr_lessen_presentieregistratie_verwerkt: sumOver('aw_m_nr_lessen_presentieregistratie_verwerkt'),
		}),
		getMultiAggregators: () => [],
	};

	createAfgeslotenLessenSignal(filter: Signal<FilterExpression>, toonAfgeslotenLessen: Signal<boolean>): Signal<MetricsData[] | undefined> {
		return toSignal(
			toObservable(filter).pipe(
				combineLatestWith(toObservable(toonAfgeslotenLessen)),
				switchMap(([f, toon]) => {
					if (!toon) return of(undefined);
					return this.dataService
						.getAanwezigheidData({
							g: [['aw_nr_maand']],
							f: this.metVerplichtePresentieregistratie(this.naarLesvarianten(this.removePresentieRegistratieVerwerktFilter(f))),
							r: [0],
							m: [AanwezigheidMeasure.LESSEN],
						})
						.pipe(
							map((resp) => {
								const { aggInit, aggCombine } = createAggFunctions(this.afgeslotenLessenAggregators, [], resp.measures);
								const records = unnest<LessenA, number[]>(resp.data, undefined, 1, aggInit, aggCombine);
								return this.processAfgeslotenLessenData(records);
							})
						);
				})
			)
		);
	}

	private removePresentieRegistratieVerwerktFilter(filter: FilterExpression): FilterExpression {
		if (filter instanceof CompoundFilterExpression) {
			return new CompoundFilterExpression(
				[
					...filter.filters
						.filter((f) => (f instanceof BasicFilterExpression ? last(f.attr) !== 'aw_is_presentieregistratie_verwerkt' : true))
						.map((f) => this.removePresentieRegistratieVerwerktFilter(f)),
				],
				filter.type
			);
		}
		return filter;
	}

	/**
	 * Zet de filters van het dashboard om naar de lesvariant; zie LESVARIANT. Filters die geen
	 * lesvariant hebben blijven zoals ze zijn: die horen bij de leerling (klas, niveau) of bij de les
	 * zelf (maand, vestiging) en betekenen in beide tabellen hetzelfde.
	 */
	private naarLesvarianten(filter: FilterExpression): FilterExpression {
		return mapFilterAttrs(filter, (attr) => LESVARIANT_PAD.get(attr.join('.')) ?? attr);
	}

	/**
	 * Het dashboard Datavolledigheid telt bij Somtoday standaard alleen lessen waarvoor presentieregistratie
	 * verplicht is. Dezelfde voorwaarde hier, zodat tegel en dashboard dezelfde percentages tonen. Staat er al
	 * een filter op dit veld, of heeft de gebruiker het gewist, dan blijft dat leidend.
	 */
	private metVerplichtePresentieregistratie(filter: FilterExpression): FilterExpression {
		if (!this.userService.isSomtoday()) return filter;
		if (flattenFilters(filter).some((f) => last(f.attr) === VERPLICHT_AFSPRAAK) || this.isVerplichtFilterGewist()) return filter;

		return new CompoundFilterExpression([filter, new BasicFilterExpression([VERPLICHT_AFSPRAAK], 1)]);
	}

	/**
	 * Een gewist filter staat als lege parameter in de url, onder de naam van dit dashboard of van een
	 * equivalent. Datavolledigheid laat dan ook zijn default achterwege, dus de tegel doet dat ook.
	 */
	private isVerplichtFilterGewist(): boolean {
		const { queryParams } = this.router.parseUrl(this.router.url);

		return this.equivalentAttService.getEquivalents(<FilterName>VERPLICHT_AFSPRAAK).some((att) => queryParams[att] === '');
	}

	private processAfgeslotenLessenData(records: Path<LessenA, number[]>[]): MetricsData[] {
		const maanden = Array.from({ length: 12 }, (_, i) => (((i + 7) % 12) + 1).toString());
		const redirectAllowed = this.autorisatieService.isUrlAllowed(DATAVOLLEDIGHEID_URL);
		return maanden.map((maand) => {
			const maandRecord = records.find((lvl) => last(lvl)!.k === maand);
			if (!maandRecord) return { percentage: null } as MetricsData;

			const afgesloten = getLeafA(maandRecord).aw_m_nr_lessen_presentieregistratie_verwerkt;
			const totaal = getLeafA(maandRecord).aw_m_nr_lessen;
			const nietAfgesloten = totaal - afgesloten;

			return {
				percentage: totaal !== 0 ? afgesloten / totaal : null,
				tooltip: [
					getMaandLabelLang(maand),
					`${formatDecimal(afgesloten)} / ${formatDecimal(totaal)} lessen afgesloten`,
					`${formatDecimal(nietAfgesloten)} ontbrekend`,
				],
				clickHandler: redirectAllowed ? () => this.redirectToDatavolledigheid(Number(maand)) : undefined,
			} as MetricsData;
		});
	}

	private redirectToDatavolledigheid(maand: number) {
		this.urlService.redirect([DATAVOLLEDIGHEID_URL], { aw_nr_maand: maand });
	}
}
